import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Badge,
  Button,
  Spinner,
  Alert,
  Form,
} from "react-bootstrap";
import {
  collection,
  query,
  where,
  onSnapshot,
  updateDoc,
  doc,
  getCountFromServer,
} from "firebase/firestore";
import { db } from "../../services/firebase";
import "./Consultations.css";
import {
  BsPersonCheck,
  BsSearch,
  BsClock,
  BsCheckCircle,
  BsTelephone,
  BsEnvelope,
  BsDownload,
  BsX,
} from "react-icons/bs";

const Consultations = () => {
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("incomplete");
  const [counts, setCounts] = useState({ active: 0, completed: 0 });
  const [filteredCount, setFilteredCount] = useState(0);

  // Fetch counts using getCountFromServer for better performance
  const fetchCounts = async () => {
    try {
      const consultationsRef = collection(db, "consultations");

      // Get incomplete count using getCountFromServer
      const incompleteQuery = query(
        consultationsRef,
        where("completed", "==", false)
      );
      const incompleteSnapshot = await getCountFromServer(incompleteQuery);

      // Get completed count using getCountFromServer
      const completedQuery = query(
        consultationsRef,
        where("completed", "==", true)
      );
      const completedSnapshot = await getCountFromServer(completedQuery);

      // Get total count using getCountFromServer
      const totalQuery = query(consultationsRef);
      const totalSnapshot = await getCountFromServer(totalQuery);

      setCounts({
        active: incompleteSnapshot.data().count,
        completed: completedSnapshot.data().count,
        total: totalSnapshot.data().count,
      });
    } catch (err) {
      console.error("Error fetching counts:", err);
      setCounts({ active: 0, completed: 0, total: 0 });
    }
  };

  // Effect for counts - fetch on component mount and when filter status changes
  useEffect(() => {
    fetchCounts();
  }, [filterStatus]);

  // Effect for consultations data
  useEffect(() => {
    const subscribeToConsultations = () => {
      setFilterLoading(true);
      const consultationsRef = collection(db, "consultations");
      const q = query(
        consultationsRef,
        where("completed", "==", filterStatus === "completed")
      );

      return onSnapshot(
        q,
        (querySnapshot) => {
          try {
            const consultationsArray = querySnapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));

            const sortedConsultations = consultationsArray.sort(
              (a, b) => b.createdAt.toDate() - a.createdAt.toDate()
            );

            setConsultations(sortedConsultations);
            setError("");
          } catch (err) {
            setError("Failed to load consultations");
            console.error("Error loading consultations:", err);
          } finally {
            setLoading(false);
            setFilterLoading(false);
          }
        },
        (error) => {
          setError("Failed to connect to database");
          setLoading(false);
          setFilterLoading(false);
          console.error("Firestore error:", error);
        }
      );
    };

    const unsubscribe = subscribeToConsultations();
    return () => unsubscribe();
  }, [filterStatus]);

  const handleStatusChange = async (consultationId, isCompleted) => {
    try {
      const consultationRef = doc(db, "consultations", consultationId);
      await updateDoc(consultationRef, {
        completed: isCompleted,
      });
      // Refresh counts after status change
      fetchCounts();
    } catch (err) {
      console.error("Error updating consultation status:", err);
    }
  };

  // Filter consultations based on search only
  const filteredConsultations = consultations.filter((consultation) => {
    const matchesSearch =
      consultation.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      consultation.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      consultation.message?.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesSearch;
  });

  // Update filtered count whenever filteredConsultations changes
  useEffect(() => {
    setFilteredCount(filteredConsultations.length);
  }, [filteredConsultations]);

  // Loading Component
  const LoadingState = () => (
    <div className="consultations-loading">
      <div className="loading-content">
        <Spinner animation="border" variant="primary" size="lg" />
        <h5 className="mt-3">Loading Consultations...</h5>
        <p className="text-muted">
          Please wait while we fetch consultation data
        </p>
      </div>
    </div>
  );

  // Filter Loading Component
  const FilterLoadingOverlay = () =>
    filterLoading && (
      <div className="filter-loading-overlay">
        <Spinner animation="border" variant="primary" size="sm" />
        <span>Filtering...</span>
      </div>
    );

  // Error Component
  const ErrorState = () => (
    <Alert variant="danger" className="consultations-error">
      <Alert.Heading>Unable to Load Consultations</Alert.Heading>
      <p>{error}</p>
      <Button variant="outline-danger" onClick={() => window.location.reload()}>
        Try Again
      </Button>
    </Alert>
  );

  // Empty State Component
  const EmptyState = () => (
    <div className="consultations-empty">
      <div className="empty-content">
        <BsPersonCheck className="empty-icon" />
        <h4>No Consultations Found</h4>
        <p className="text-muted">
          {searchQuery
            ? "No consultations match your search."
            : `No ${filterStatus} consultations found.`}
        </p>
        {searchQuery && (
          <Button variant="outline-primary" onClick={() => setSearchQuery("")}>
            Clear Search
          </Button>
        )}
      </div>
    </div>
  );

  if (loading) return <LoadingState />;
  if (error) return <ErrorState />;

  return (
    <div className="consultations-container">
      {/* Header Section */}
      <div className="consultations-header">
        <div className="header-content">
          <div className="header-left">
            <BsPersonCheck className="header-icon" />
            <div>
              <h4 className="header-title">Client Consultations</h4>
              <p className="header-subtitle">
                Manage incoming consultation requests
              </p>
            </div>
          </div>
          <div className="header-right">
            <div className="stats-badges">
              <Badge bg="warning" pill className="stat-badge">
                {counts.active} Incomplete
              </Badge>
              <Badge bg="success" pill className="stat-badge">
                {counts.completed} Completed
              </Badge>
              <Badge bg="info" pill className="stat-badge">
                {counts.total} Total
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Search & Filter Section */}
      <div className="advanced-search-section">
        <div className="search-filter-container">
          {/* Professional Search Bar */}
          <div className="professional-search-wrapper">
            <div className="search-input-group">
              <BsSearch className="consultations-search-icon" />
              <input
                type="text"
                placeholder="Search consultations by name, email, or message content..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="professional-search-input"
                autoComplete="off"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="search-clear-button"
                  aria-label="Clear search"
                >
                  <BsX />
                </button>
              )}
            </div>
          </div>

          {/* Filter Buttons */}
          <div className="filter-tabs-container">
            <div className="filter-tabs-wrapper">
              <button
                type="button"
                className={`filter-tab ${
                  filterStatus === "incomplete" ? "active" : ""
                }`}
                onClick={() => setFilterStatus("incomplete")}
                disabled={filterLoading}
              >
                <div className="tab-content">
                  <BsClock className="tab-icon" />
                  <span className="tab-label">Incomplete</span>
                  <span className="tab-count">{counts.active}</span>
                </div>
              </button>
              <button
                type="button"
                className={`filter-tab ${
                  filterStatus === "completed" ? "active" : ""
                }`}
                onClick={() => setFilterStatus("completed")}
                disabled={filterLoading}
              >
                <div className="tab-content">
                  <BsCheckCircle className="tab-icon" />
                  <span className="tab-label">Completed</span>
                  <span className="tab-count">{counts.completed}</span>
                </div>
              </button>
              {filterLoading && (
                <div className="filter-loading">
                  <Spinner animation="border" size="sm" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="consultations-content">
        <FilterLoadingOverlay />
        {filteredConsultations.length === 0 ? (
          <EmptyState />
        ) : (
          <Row xs={1} md={2} lg={3} className="g-4">
            {filteredConsultations.map((consultation) => (
              <Col key={consultation.id}>
                <Card
                  className={`consultation-card ${
                    consultation.completed ? "completed" : "incomplete"
                  }`}
                >
                  <Card.Body>
                    <div className="card-header-section">
                      <div className="client-info">
                        <h6 className="client-name">{consultation.name}</h6>
                        <div className="status-section">
                          <Form.Check
                            type="switch"
                            id={`switch-${consultation.id}`}
                            checked={consultation.completed || false}
                            onChange={(e) =>
                              handleStatusChange(
                                consultation.id,
                                e.target.checked
                              )
                            }
                            className="status-switch"
                            label={
                              consultation.completed
                                ? "Completed"
                                : "Incomplete"
                            }
                          />
                        </div>
                      </div>
                      <div className="timestamp">
                        <BsClock className="clock-icon" />
                        <small className="text-muted">
                          {consultation.createdAt
                            ?.toDate()
                            .toLocaleDateString()}
                        </small>
                      </div>
                    </div>

                    <div className="contact-section">
                      <div className="contact-item">
                        <BsEnvelope className="contact-icon" />
                        <span className="contact-text">
                          {consultation.email}
                        </span>
                      </div>
                      {consultation.phone && (
                        <div className="contact-item">
                          <BsTelephone className="contact-icon" />
                          <span className="contact-text">
                            {consultation.phone}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="message-section">
                      <div className="message-header">
                        <span className="message-label">Message</span>
                      </div>
                      <div className="consultation-message-content">
                        {consultation.message}
                      </div>
                    </div>

                    <div className="card-footer-section">
                      <div className="action-buttons">
                        {consultation.mediaUrl && (
                          <Button
                            size="sm"
                            variant="outline-primary"
                            href={consultation.mediaUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="action-btn"
                          >
                            <BsDownload className="me-1" />
                            Download
                          </Button>
                        )}
                      </div>
                      <div className="status-indicator">
                        <div
                          className={`status-dot ${
                            consultation.completed ? "completed" : "incomplete"
                          }`}
                        />
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </div>

      {/* Results Info */}
      {filteredConsultations.length > 0 && (
        <div className="results-info">
          <p className="text-muted">
            Showing {filteredCount} of {counts.total} consultations
            {searchQuery && ` matching "${searchQuery}"`}
          </p>
        </div>
      )}
    </div>
  );
};

export default Consultations;
